import { afterNextRender, afterRenderEffect, Component, DestroyRef, ElementRef, inject, signal, viewChild, viewChildren } from '@angular/core';
import { RouterLink } from '@angular/router';
import { IconComponent } from '@cumlaude/shared-components-icons';
import { TooltipDirective } from '@cumlaude/shared-components-overlays';
import { BreadcrumbService } from '../../../services/breadcrumb.service';
import { Crumb, truncate } from './breadcrumb-model';

const GAP_NA_HUISICOON = 4;
const GAP_TUSSEN_CRUMBS = 8;

export type VisibleCrumb = Crumb & { readonly truncated: boolean };

/**
 * Het pad-gedeelte van de paginakop: huis-icoon + het volledige, getruncate breadcrumb-pad
 * (CL-9211). De crumbs zelf (inclusief welke klikbaar zijn) komen kant-en-klaar uit
 * BreadcrumbService; dit component doet alleen de rendering en de truncatie.
 *
 * Truncatie is breedte-gestuurd: een verborgen `.meten`-lijst rendert alle crumbs ongekort (plus
 * een losse "…"-sample) zodat hun werkelijke breedte gemeten kan worden, ongeacht welke al
 * getruncate zijn in de zichtbare lijst -- zonder die verborgen lijst zou een ResizeObserver op de
 * host zijn eigen trigger worden zodra de zichtbare lijst smaller/breder wordt door truncatie.
 */
@Component({
	selector: 'app-breadcrumb',
	templateUrl: './breadcrumb.component.html',
	styleUrls: ['./breadcrumb.component.scss'],
	imports: [RouterLink, IconComponent, TooltipDirective],
})
export class BreadcrumbComponent {
	private readonly breadcrumbService = inject(BreadcrumbService);
	private readonly hostRef = inject(ElementRef<HTMLElement>);
	private readonly destroyRef = inject(DestroyRef);

	protected readonly crumbs = this.breadcrumbService.crumbs;

	private readonly homeLi = viewChild<ElementRef<HTMLLIElement>>('homeLi');
	private readonly meetEllipsis = viewChild<ElementRef<HTMLLIElement>>('meetEllipsis');
	private readonly meetCrumbs = viewChildren<ElementRef<HTMLLIElement>>('meetCrumb');

	private readonly beschikbareBreedte = signal(0);

	protected readonly visibleCrumbs = signal<readonly VisibleCrumb[]>([]);

	constructor() {
		afterNextRender(() => {
			const resizeObserver = new ResizeObserver(([entry]) => this.beschikbareBreedte.set(entry.contentRect.width));
			resizeObserver.observe(this.hostRef.nativeElement);
			this.destroyRef.onDestroy(() => resizeObserver.disconnect());
		});

		afterRenderEffect(() => {
			const crumbs = this.crumbs();
			const meetCrumbs = this.meetCrumbs();
			const ellipsis = this.meetEllipsis()?.nativeElement;
			const home = this.homeLi()?.nativeElement;
			const beschikbaar = this.beschikbareBreedte();

			if (!ellipsis || !home || meetCrumbs.length !== crumbs.length) return;

			const volledigeBreedtes = meetCrumbs.map((ref) => ref.nativeElement.offsetWidth);
			const getruncateBreedte = ellipsis.offsetWidth;
			const beschikbaarVoorCrumbs = beschikbaar - home.offsetWidth - GAP_NA_HUISICOON - GAP_TUSSEN_CRUMBS * Math.max(crumbs.length - 1, 0);

			const getruncateIndices = truncate(volledigeBreedtes, getruncateBreedte, beschikbaarVoorCrumbs);
			this.visibleCrumbs.set(crumbs.map((crumb, i) => ({ ...crumb, truncated: getruncateIndices.has(i) })));
		});
	}
}
